import { useCallback, useEffect, useState } from "react";
import api from "../services/api";

/**
 * usePaidAccess
 *
 * Verifica se o usuário logado tem acesso pago (Plus) — só relevante pra
 * aluno self-service sem professor (modelo B2C Free/Plus, ver
 * docs/01-produto.md). Professor, admin e aluno com professor nunca são
 * gateados, então nem chamam a API — evita round-trip desnecessário.
 *
 * Mesma regra usada em PaymentsModal.jsx e no backend (app/services/payments/access.py):
 * assinatura ativa, assinatura cancelada mas ainda dentro do período pago,
 * ou pagamento avulso com expires_at no futuro.
 *
 * @param {object} me - usuário logado (role, is_self_service, professor_id)
 * @returns {{ hasPaidAccess: boolean, loading: boolean, refetch: () => void }}
 */
export function usePaidAccess(me) {
  const isGatedSelfService = me?.role === "aluno" && me?.is_self_service && !me?.professor_id;

  const [hasPaidAccess, setHasPaidAccess] = useState(!isGatedSelfService);
  const [loading, setLoading] = useState(isGatedSelfService);

  const fetchAccess = useCallback(() => {
    if (!isGatedSelfService) {
      setHasPaidAccess(true);
      setLoading(false);
      return () => {};
    }

    let cancelled = false;
    setLoading(true);
    Promise.all([api.get("/payments/access"), api.get("/payments/subscriptions/me")])
      .then(([accessRes, subRes]) => {
        if (cancelled) return;
        const oneOffAccess = accessRes.data;
        const subscription = subRes.data;
        const now = new Date();

        // A Asaas marca Subscription.status = "active" já na CRIAÇÃO da
        // assinatura - antes até do Pix ser escaneado (Pix não é débito
        // automático, ver access.py::has_paid_access). Sem o check de
        // last_payment, todo aluno que gerava uma assinatura Pix e nunca
        // pagava já via a coroa/menu do Plus liberados no front (mesmo o
        // backend rejeitando com 403 na hora de usar de verdade - nutrition.py,
        // body_measurements.py etc chamam require_plus_access, que exige um
        // Payment received/confirmed). last_payment só vem preenchido pelo
        // backend quando existe esse pagamento real (ver
        // app/api/payments/subscriptions.py::_last_payment_dict) - mesmo
        // sinal que access.py::_subscription_has_paid_cycle usa.
        const hasPaidCycle = !!subscription?.last_payment;
        const activeSubscription = subscription && subscription.status === "active" && hasPaidCycle;
        const canceledButValidSubscription =
          subscription && subscription.status === "canceled" && hasPaidCycle &&
          subscription.current_period_end && new Date(subscription.current_period_end) > now;
        const activeOneOff = oneOffAccess?.expires_at && new Date(oneOffAccess.expires_at) > now;

        setHasPaidAccess(!!(activeSubscription || canceledButValidSubscription || activeOneOff));
      })
      .catch(() => {
        if (!cancelled) setHasPaidAccess(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGatedSelfService, me?.id]);

  useEffect(() => {
    return fetchAccess();
  }, [fetchAccess]);

  return { hasPaidAccess, loading, refetch: fetchAccess };
}
